import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Copy, MapPin, DollarSign, Calendar, Heart, Send, Star, Paperclip, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import ProjectAttachmentsModal from '../components/ProjectAttachmentsModal';
import FavoriteQualificationModal from '../components/FavoriteQualificationModal';
import PreReservationModal from '../components/PreReservationModal';
import SearchAndViewToggle from '../components/SearchAndViewToggle';
import ProjectListView from '../components/ProjectListView';
import BonusTrialModal from '../components/BonusTrialModal';
import { getCertificationAcronym } from '../constants/certifications';
import { 
  LANGUAGES, 
  PROJECT_TYPES, 
  COMMERCIAL_MODELS,
  BUDGET_TYPES,
  OCCUPATION_RATES,
  REMOTE_PERCENTAGES,
  COUNTRIES,
  CURRENCY_OPTIONS,
  PRICE_TYPES,
  DURATION_UNITS,
  REMOTE_ZONES,
  REGIONS,
  CECRL_LEVELS
} from '../constants/projectData';
import {
  SERVICENOW_ROLES,
  SERVICENOW_MODULES,
  SERVICENOW_PRODUCTS,
  SECURITY_CLEARANCE_LEVELS,
  CLEARANCE_COUNTRIES
} from '../utils/servicenowData';
import { SERVICENOW_CERTIFICATIONS } from '../constants/projectData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsPageNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  // Matches dialog removed - now redirects directly to matching page
  const [favorites, setFavorites] = useState(new Set());
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [selectedProjectForAttachments, setSelectedProjectForAttachments] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [showFavoriteModal, setShowFavoriteModal] = useState(null); // For qualification modal
  const [showPreReservationModal, setShowPreReservationModal] = useState(null); // {projectId, resourceId, resourceName, projectTitle}
  
  // Edit modal state
  const [editMode, setEditMode] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  
  // Bonus trial modal state
  const [showBonusTrialModal, setShowBonusTrialModal] = useState(false);
  const [bonusTrialOffer, setBonusTrialOffer] = useState(null);
  const [pendingActivationProjectId, setPendingActivationProjectId] = useState(null);
  const [bonusTrialLoading, setBonusTrialLoading] = useState(false);
  
  // Search and view mode states
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  
  // Get tomorrow's date as default start date
  const getDefaultStartDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    required_skills: '',
    required_certifications: [],
    other_certifications: '',
    servicenow_roles: [],  // New: ServiceNow roles required
    servicenow_modules: [],  // New: ServiceNow modules to master
    servicenow_products: [],  // NEW: Primary matching signal (Products)
    number_of_consultants: 1,
    languages_required: [],  // Array of {language, level} objects
    
    // Budget - New structure
    currency: '', // Empty by default, user must select
    price_amount: '',
    price_type: '', // Empty by default, user must select
    hide_budget: false, // NEW: Hide budget from RMs
    
    // Duration - New structure
    duration_value: '',
    duration_unit: '', // Empty by default, user must select
    
    // Equipment
    equipment_provided: false,
    
    // Remote work - Enhanced
    remote_possible: false,
    remote_percentage: 0,
    remote_zone: '', // Empty by default, user must select
    remote_zone_detail: '',
    timezone_restrictions: '',
    
    // NEW: Consultant Preferences
    prefer_ai_augmented: false,
    
    // NEW: Security Requirements
    has_security_requirement: false,
    required_clearance_level: null,
    clearance_valid_country: null,
    requires_local_presence: false,
    nationality_restriction: null,
    
    // Old fields (kept for backward compatibility)
    effort_hours: '',
    effort_days: '',
    effort_weeks: '',
    effort_months: '',
    occupation_rate: 100,
    budget_type: 'Daily',
    budget: '',
    duration: '',
    
    project_type: 'Implementation',
    commercial_model: 'Time & Material',
    city: '',
    country: '',
    start_date: getDefaultStartDate(),  // Default to tomorrow
    is_activated: false  // New field for immediate activation
  });

  const isPM = user?.role === 'project_manager';
  
  // Filter projects based on search query
  const filteredMyProjects = useMemo(() => {
    if (!searchQuery.trim()) return myProjects;
    const query = searchQuery.toLowerCase();
    return myProjects.filter(p => 
      (p.title && p.title.toLowerCase().includes(query)) ||
      (p.description && p.description.toLowerCase().includes(query)) ||
      (p.city && p.city.toLowerCase().includes(query)) ||
      (p.country && p.country.toLowerCase().includes(query)) ||
      (p.project_type && p.project_type.toLowerCase().includes(query)) ||
      (p.required_skills && typeof p.required_skills === 'string' && p.required_skills.toLowerCase().includes(query))
    );
  }, [myProjects, searchQuery]);

  useEffect(() => {
    fetchProjects();
    if (isPM) {
      fetchMyProjects();
      fetchFavorites();
    }
    
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'create' && isPM) {
      setShowDialog(true);
    }
  }, [location.search]);

  // Handle edit project from navigation state (from ProjectDetailPage)
  useEffect(() => {
    if (location.state?.editProjectId && myProjects.length > 0) {
      const projectToEdit = myProjects.find(p => p.id === location.state.editProjectId);
      if (projectToEdit) {
        handleEditProject(projectToEdit);
        // Clear the state to prevent re-opening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, myProjects]);

  const fetchProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort projects: Active first, then by creation date (newest first)
      const sorted = response.data.sort((a, b) => {
        // Active projects first
        if (a.is_activated !== b.is_activated) {
          return a.is_activated ? -1 : 1;
        }
        // Then by creation date (newest first)
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setMyProjects(sorted);
    } catch (error) {
      toast.error('Failed to load your projects');
    }
  };

  const fetchFavorites = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/favorites/resources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const favSet = new Set(response.data.map(r => r.id));
      setFavorites(favSet);
    } catch (error) {
      console.error('Failed to load favorites');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    // Set default start_date if not provided (tomorrow's date)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultStartDate = tomorrow.toISOString().split('T')[0]; // Format: YYYY-MM-DD

    const payload = {
      ...formData,
      // Use modules as required_skills for backward compatibility
      required_skills: formData.servicenow_modules && formData.servicenow_modules.length > 0 
        ? formData.servicenow_modules 
        : ['ITSM'], // Default skill if no modules selected
      // Ensure start_date has a value
      start_date: formData.start_date || defaultStartDate,
      // New budget structure
      price_amount: parseFloat(formData.price_amount),
      // Old budget for backward compatibility
      budget: parseFloat(formData.price_amount),
      budget_type: formData.price_type === 'hourly' ? 'Hourly' : 
                   formData.price_type === 'daily' ? 'Daily' :
                   formData.price_type === 'monthly' ? 'Monthly' : 'Full Project',
      // Duration fields
      duration_value: formData.duration_value ? parseFloat(formData.duration_value) : null,
      // Old effort fields
      effort_hours: formData.effort_hours ? parseFloat(formData.effort_hours) : null,
      effort_days: formData.effort_days ? parseFloat(formData.effort_days) : null,
      effort_weeks: formData.effort_weeks ? parseFloat(formData.effort_weeks) : null,
      effort_months: formData.effort_months ? parseFloat(formData.effort_months) : null,
    };

    try {
      const response = await axios.post(`${API}/projects`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newProjectId = response.data.id;
      
      // Upload attachments if any
      if (pendingAttachments.length > 0) {
        toast.info(`Uploading ${pendingAttachments.length} attachment(s)...`);
        
        for (const file of pendingAttachments) {
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            await axios.post(`${API}/projects/${newProjectId}/upload-attachment`, formData, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            });
          } catch (uploadError) {
            console.error('Failed to upload attachment:', file.name, uploadError);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
        
        if (pendingAttachments.length > 0) {
          toast.success(`Project created with ${pendingAttachments.length} attachment(s)! ${payload.is_activated ? '🟢 Now visible to Resource Managers.' : '📝 Saved as draft - activate it when ready.'}`);
        } else {
          toast.success(payload.is_activated ? 'Project created and activated! 🟢 Now visible to Resource Managers.' : 'Project created as draft! 📝 Activate it when you\'re ready to make it visible.');
        }
      } else {
        toast.success(payload.is_activated ? 'Project created and activated! 🟢 Now visible to Resource Managers.' : 'Project created as draft! 📝 Activate it when you\'re ready to make it visible.');
      }
      
      setShowDialog(false);
      resetForm();
      setPendingAttachments([]);
      fetchMyProjects();
      fetchProjects();
      
      // Show matches only if activated
      if (payload.is_activated) {
        viewMatches(newProjectId);
      }
    } catch (error) {
      console.error('Project creation error:', error);
      // Handle validation errors (422) differently from other errors
      if (error.response?.status === 422 && Array.isArray(error.response?.data?.detail)) {
        const errorMessages = error.response.data.detail.map(err => 
          `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`
        ).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else if (error.response?.data?.detail) {
        // Handle string detail
        toast.error(typeof error.response.data.detail === 'string' ? error.response.data.detail : 'Operation failed');
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const viewMatches = async (projectId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects/${projectId}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Always redirect to matching page for consistent workflow
      const matchCount = response.data?.length || 0;
      const perfectMatches = response.data?.filter(m => m.match_score === 100).length || 0;
      
      if (perfectMatches > 0) {
        toast.success(`✨ ${perfectMatches} perfect match${perfectMatches > 1 ? 'es' : ''} found!`, {
          description: 'Opening matching page...'
        });
      } else if (matchCount > 0) {
        toast.info(`${matchCount} potential match${matchCount > 1 ? 'es' : ''} found`, {
          description: 'Opening matching page...'
        });
      } else {
        toast.info('No exact matches found. Showing broader results...');
      }
      
      // Navigate to matching page with project pre-selected
      setTimeout(() => {
        navigate(`/matching?project=${projectId}`);
      }, 800);
    } catch (error) {
      toast.error('Failed to load matches');
    }
  };

  // Handle edit project from list view
  const handleEditProject = (project) => {
    // Populate formData with project data for editing
    setFormData({
      title: project.title || '',
      description: project.description || '',
      required_skills: project.required_skills || '',
      required_certifications: project.required_certifications || [],
      other_certifications: project.other_certifications || '',
      servicenow_roles: project.servicenow_roles || [],
      servicenow_modules: project.servicenow_modules || [],
      servicenow_products: project.servicenow_products || [],
      number_of_consultants: project.number_of_consultants || 1,
      languages_required: project.languages_required || [],
      currency: project.currency || '',
      price_amount: project.price_amount || '',
      price_type: project.price_type || '',
      hide_budget: project.hide_budget || false,
      duration_value: project.duration_value || '',
      duration_unit: project.duration_unit || '',
      equipment_provided: project.equipment_provided || false,
      remote_possible: project.remote_possible || false,
      remote_percentage: project.remote_percentage || 0,
      remote_zone: project.remote_zone || '',
      remote_zone_detail: project.remote_zone_detail || '',
      timezone_restrictions: project.timezone_restrictions || '',
      prefer_ai_augmented: project.prefer_ai_augmented || false,
      has_security_requirement: project.has_security_requirement || false,
      required_clearance_level: project.required_clearance_level || null,
      clearance_valid_country: project.clearance_valid_country || null,
      requires_local_presence: project.requires_local_presence || false,
      nationality_restriction: project.nationality_restriction || null,
      effort_hours: project.effort_hours || '',
      effort_days: project.effort_days || '',
      effort_weeks: project.effort_weeks || '',
      effort_months: project.effort_months || '',
      occupation_rate: project.occupation_rate || 100,
      project_type: project.project_type || 'Implementation',
      commercial_model: project.commercial_model || 'Time & Material',
      budget_type: project.budget_type || 'Daily',
      budget: project.budget || '',
      city: project.city || '',
      country: project.country || '',
      region: project.region || '',
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      duration: project.duration || '',
      is_activated: project.is_activated || false
    });
    setEditingProject(project);
    setEditMode(true);
    setShowDialog(true);
  };

  // Handle saving an edited project
  const handleSaveEditedProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultStartDate = tomorrow.toISOString().split('T')[0];

    const payload = {
      ...formData,
      required_skills: formData.servicenow_modules && formData.servicenow_modules.length > 0 
        ? formData.servicenow_modules 
        : ['ITSM'],
      start_date: formData.start_date || defaultStartDate,
      price_amount: parseFloat(formData.price_amount) || null,
      budget: parseFloat(formData.price_amount) || null,
      budget_type: formData.price_type === 'hourly' ? 'Hourly' : 
                   formData.price_type === 'daily' ? 'Daily' :
                   formData.price_type === 'monthly' ? 'Monthly' : 'Full Project',
      duration_value: formData.duration_value ? parseFloat(formData.duration_value) : null,
      effort_hours: formData.effort_hours ? parseFloat(formData.effort_hours) : null,
      effort_days: formData.effort_days ? parseFloat(formData.effort_days) : null,
      effort_weeks: formData.effort_weeks ? parseFloat(formData.effort_weeks) : null,
      effort_months: formData.effort_months ? parseFloat(formData.effort_months) : null,
    };

    try {
      await axios.put(`${API}/projects/${editingProject.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project updated successfully');
      handleEditModalClose();
    } catch (error) {
      console.error('Project update error:', error);
      if (error.response?.status === 422 && Array.isArray(error.response?.data?.detail)) {
        const errorMessages = error.response.data.detail.map(err => 
          `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`
        ).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update project');
      }
    }
  };

  // Handle edit modal close and refresh
  const handleEditModalClose = () => {
    setEditMode(false);
    setEditingProject(null);
    setShowDialog(false);
    resetForm();
    fetchMyProjects();
    fetchProjects();
  };

  const toggleProjectActivation = async (projectId, currentStatus) => {
    const token = localStorage.getItem('token');
    const endpoint = currentStatus ? 'deactivate' : 'activate';
    
    try {
      await axios.post(`${API}/projects/${projectId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Project ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchMyProjects();
      fetchProjects();
    } catch (error) {
      const errorData = error.response?.data?.detail;
      
      // Check if this is a bonus trial offer
      if (error.response?.status === 402 && errorData?.error === 'bonus_trial_available') {
        setBonusTrialOffer(errorData.bonus_trial_offer);
        setPendingActivationProjectId(projectId);
        setShowBonusTrialModal(true);
        return;
      }
      
      // Handle other errors
      if (typeof errorData === 'object' && errorData?.message) {
        toast.error(errorData.message);
      } else {
        toast.error(errorData || 'Failed to update project status');
      }
    }
  };

  const handleAcceptBonusTrial = async () => {
    const token = localStorage.getItem('token');
    setBonusTrialLoading(true);
    
    try {
      // Accept the bonus trial
      const response = await axios.post(`${API}/subscription/accept-bonus-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || '🎉 Advanced trial activated!');
      setShowBonusTrialModal(false);
      setBonusTrialOffer(null);
      
      // Now activate the project
      if (pendingActivationProjectId) {
        await axios.post(`${API}/projects/${pendingActivationProjectId}/activate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Project activated successfully!');
        setPendingActivationProjectId(null);
      }
      
      // Refresh data
      fetchMyProjects();
      fetchProjects();
      
      // Refresh user data to update subscription info
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate bonus trial');
    } finally {
      setBonusTrialLoading(false);
    }
  };

  const toggleFavorite = async (resourceId, resourceName) => {
    console.log('🔍 toggleFavorite called with resourceId:', resourceId);
    console.log('🔍 Current favorites:', Array.from(favorites));
    const token = localStorage.getItem('token');
    try {
      if (favorites.has(resourceId)) {
        console.log('❌ Removing from favorites...');
        await axios.delete(`${API}/favorites/resource/${resourceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(resourceId);
          return newSet;
        });
        toast.success('Removed from favorites');
      } else {
        console.log('➕ Adding to favorites...');
        const response = await axios.post(`${API}/favorites/resource/${resourceId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => new Set(prev).add(resourceId));
        
        // Show qualification modal
        setShowFavoriteModal({
          resourceId,
          resourceName,
          favoriteId: response.data.favorite_id || response.data.id
        });
      }
      console.log('✅ toggleFavorite completed successfully');
    } catch (error) {
      console.error('❌ toggleFavorite error:', error);
      toast.error('Failed to update favorites');
    }
  };

  const handleSaveFavoriteQualification = async (data) => {
    const token = localStorage.getItem('token');
    try {
      if (showFavoriteModal?.favoriteId) {
        console.log('🔍 Saving favorite qualification:', data);
        console.log('🔍 Favorite ID:', showFavoriteModal.favoriteId);
        
        // Update tags
        if (data.tags && data.tags.length > 0) {
          const tagsResponse = await axios.put(`${API}/favorites/${showFavoriteModal.favoriteId}/tags`, {
            tags: data.tags
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('✅ Tags updated:', tagsResponse.data);
        }
        
        // Update notes
        if (data.notes) {
          const notesResponse = await axios.put(`${API}/favorites/${showFavoriteModal.favoriteId}/notes`, {
            private_notes: data.notes
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('✅ Notes updated:', notesResponse.data);
        }
        
        toast.success('Favorite qualified successfully');
      }
    } catch (error) {
      console.error('❌ Failed to update favorite qualification:', error);
      toast.error('Failed to save qualification');
    } finally {
      setShowFavoriteModal(null);
    }
  };

  const preReserveResource = (projectId, resourceId, resourceName = null, projectTitle = null) => {
    console.log('🔍 Opening pre-reservation modal');
    console.log('🔍 projectId:', projectId);
    console.log('🔍 resourceId:', resourceId);
    
    // Find project details if not provided
    const project = projects.find(p => p.id === projectId);
    
    setShowPreReservationModal({
      projectId,
      resourceId,
      resourceName: resourceName || 'Resource',
      projectTitle: projectTitle || project?.title || 'Project'
    });
  };

  const handleConfirmPreReservation = async (customMessage) => {
    if (!showPreReservationModal) return;
    
    const { projectId, resourceId } = showPreReservationModal;
    const token = localStorage.getItem('token');
    
    try {
      console.log('📤 Sending pre-reservation request with custom message...');
      await axios.post(`${API}/pre-reservations`, null, {
        params: { 
          project_id: projectId, 
          resource_id: resourceId,
          custom_message: customMessage || undefined
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ Pre-reservation request sent successfully');
      toast.success('Pre-reservation request sent');
      setShowPreReservationModal(null);
    } catch (error) {
      console.error('❌ preReserveResource error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send pre-reservation');
    }
  };

  // Handle delete project
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted successfully');
      fetchMyProjects();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  // Handle duplicate project
  const handleDuplicateProject = async (project) => {
    const token = localStorage.getItem('token');
    try {
      const duplicateData = {
        ...project,
        title: project.title + ' (Copy)',
        project_id: undefined,
        id: undefined,
        is_activated: false,
        created_at: undefined,
        attachments: undefined
      };

      delete duplicateData._id;
      delete duplicateData.id;
      delete duplicateData.project_id;

      await axios.post(`${API}/projects`, duplicateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Project duplicated successfully (inactive)');
      fetchMyProjects();
      fetchProjects();
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error(error.response?.data?.detail || 'Failed to duplicate project');
    }
  };

  const resetForm = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultStartDate = tomorrow.toISOString().split('T')[0];
    
    setFormData({
      title: '',
      description: '',
      required_skills: '',
      required_certifications: [],
      other_certifications: '',
      number_of_consultants: 1,
      languages_required: [],
      effort_hours: '',
      effort_days: '',
      effort_weeks: '',
      effort_months: '',
      occupation_rate: 100,
      project_type: 'Implementation',
      commercial_model: 'Time & Material',
      budget_type: 'Daily',
      budget: '',
      city: '',
      country: '',
      remote_possible: false,
      remote_percentage: 0,
      start_date: defaultStartDate,  // Reset to tomorrow
      duration: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="projects-page">

      <main className="max-w-7xl mx-auto px-6 py-8 ">
        <div className="flex items-center justify-between mb-8 fade-in">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B3240' }}>Projects</h1>
            <p className="text-lg text-gray-600">Browse and manage ServiceNow projects</p>
          </div>
          {isPM && (
            <Button 
              className="btn-snow"
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
              data-testid="add-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          )}
        </div>

        {/* My Projects */}
        {isPM && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold" style={{ color: '#1B3240' }}>My Projects</h2>
              <span className="text-sm text-gray-500">{filteredMyProjects.length} project(s)</span>
            </div>
            
            {/* Search and View Toggle */}
            {myProjects.length > 0 && (
              <SearchAndViewToggle
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                placeholder="Search by title, location, type, skills..."
              />
            )}
            
            {/* List View */}
            {viewMode === 'list' && myProjects.length > 0 ? (
              <ProjectListView
                projects={filteredMyProjects}
                isOwner={true}
                onToggleActivation={toggleProjectActivation}
                onEdit={handleEditProject}
                onNavigate={(id) => navigate(`/projects/${id}`)}
              />
            ) : (
            /* Card View */
            <div className="grid md:grid-cols-2 gap-6">
              {filteredMyProjects.length === 0 && searchQuery ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                  No projects match your search "{searchQuery}"
                </div>
              ) : myProjects.length === 0 ? (
                <Card 
                  className="card-hover cursor-pointer border-2 border-dashed border-slate-300 hover:border-slate-400 transition-colors bg-white"
                  onClick={() => setShowDialog(true)}
                >
                  <CardContent className="py-16 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                        <Plus className="w-8 h-8 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        Create your first project
                      </h3>
                      <p className="text-sm text-slate-600">
                        Start by posting a project to find the perfect ServiceNow consultant
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                filteredMyProjects.map((project) => (
                  <Card 
                    key={project.id} 
                    className="card-hover hover:shadow-lg transition-all duration-200 bg-white border border-gray-200 relative cursor-pointer"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    {/* Toggle Active/Inactive - Top Right like resource cards */}
                    <div className="absolute top-4 right-4 z-10" onClick={(e) => e.stopPropagation()}>
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={project.is_activated}
                            onChange={() => toggleProjectActivation(project.id, project.is_activated)}
                          />
                          <div className={`block w-10 h-6 rounded-full transition-colors ${
                            project.is_activated ? 'bg-green-500' : 'bg-gray-300'
                          }`}></div>
                          <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                            project.is_activated ? 'transform translate-x-4' : ''
                          }`}></div>
                        </div>
                      </label>
                    </div>

                    <CardHeader className="pb-3 pr-16">
                      <div className="flex items-start gap-2 mb-2">
                        <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md border border-blue-200">
                          {project.project_id}
                        </span>
                        {project.is_activated && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-md border border-green-200">
                            <Star className="w-3 h-3 fill-current" />
                            Active
                          </span>
                        )}
                        {/* Quick Match Icon */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewMatches(project.id);
                          }}
                          className="h-7 px-2 ml-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                          title="Find matching consultants"
                          data-testid={`view-matches-${project.id}-btn`}
                        >
                          <Star className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <CardTitle className="text-xl font-bold mb-1" style={{ color: '#1B3240' }}>
                        {project.title}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-600 font-medium">
                          {project.project_type} • {project.commercial_model || 'T&M'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-500">
                          {project.number_of_consultants} consultant{project.number_of_consultants > 1 ? 's' : ''} • {project.occupation_rate}% occupation
                        </p>
                        {project.prefer_ai_augmented && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-sm">
                            AI-Preferred
                          </span>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0 space-y-3">
                      {/* Description */}
                      <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>

                      {/* ServiceNow Products */}
                      {project.servicenow_products && project.servicenow_products.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">Products Required</p>
                          <div className="flex flex-wrap gap-2">
                            {project.servicenow_products.slice(0, 3).map((productId, idx) => (
                              <span key={idx} className="px-2 py-1 text-xs font-medium rounded-md bg-purple-50 text-purple-700 border border-purple-200">
                                {productId}
                              </span>
                            ))}
                            {project.servicenow_products.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-md border border-gray-300">
                                +{project.servicenow_products.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Languages with flags */}
                      {project.languages_required && project.languages_required.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">Languages</p>
                          <div className="flex flex-wrap gap-2">
                            {project.languages_required.slice(0, 3).map((lang, idx) => {
                              const flagEmojis = {
                                'English': '🇬🇧', 'French': '🇫🇷', 'German': '🇩🇪', 'Spanish': '🇪🇸',
                                'Italian': '🇮🇹', 'Portuguese': '🇵🇹', 'Dutch': '🇳🇱', 'Russian': '🇷🇺',
                                'Chinese (Mandarin)': '🇨🇳', 'Japanese': '🇯🇵', 'Korean': '🇰🇷'
                              };
                              return (
                                <span key={idx} className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-700 border border-gray-200 flex items-center gap-1">
                                  <span>{flagEmojis[lang.language] || '🌐'}</span>
                                  {typeof lang === 'string' ? lang : `${lang.language} • ${lang.level}`}
                                </span>
                              );
                            })}
                            {project.languages_required.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                +{project.languages_required.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Certifications */}
                      {project.required_certifications && project.required_certifications.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">Certifications Required</p>
                          <div className="flex flex-wrap gap-2">
                            {project.required_certifications.slice(0, 3).map((cert, idx) => (
                              <span key={idx} className="px-2 py-1 text-xs font-medium rounded-md border" style={{ 
                                background: 'rgba(98, 216, 78, 0.1)', 
                                color: '#62D84E',
                                borderColor: 'rgba(98, 216, 78, 0.3)'
                              }}>
                                {getCertificationAcronym(cert)}
                              </span>
                            ))}
                            {project.required_certifications.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-md border border-gray-300">
                                +{project.required_certifications.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Location & Remote */}
                      <div className="flex items-center justify-between py-2 border-t border-gray-100">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center text-sm text-gray-700">
                            <MapPin className="w-4 h-4 mr-1 text-gray-500" />
                            <span className="font-medium">
                              {project.city || 'Not specified'}, {project.country || ''}
                            </span>
                          </div>
                        </div>
                        {project.remote_possible && (
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md border border-blue-200">
                            {project.remote_percentage}% Remote
                          </span>
                        )}
                      </div>

                      {/* Timeline & Budget */}
                      <div className="flex items-center justify-between py-2 border-t border-gray-100">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center text-sm text-gray-700">
                            <Calendar className="w-4 h-4 mr-1 text-gray-500" />
                            <span>
                              {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'TBD'}
                            </span>
                          </div>
                          {project.duration_value && project.duration_unit && (
                            <div className="text-xs text-gray-600">
                              Duration: {project.duration_value} {project.duration_unit}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center text-base font-bold" style={{ color: '#1B3240' }}>
                            <DollarSign className="w-4 h-4 mr-1" />
                            {project.price_amount && project.currency && project.price_type ? (
                              <>
                                {project.currency === 'EUR' ? '€' : project.currency === 'USD' ? '$' : project.currency === 'GBP' ? '£' : project.currency}
                                {project.price_amount.toLocaleString()} / {project.price_type}
                              </>
                            ) : project.budget && project.budget_type ? (
                              <>€{project.budget.toLocaleString()} / {project.budget_type.toLowerCase()}</>
                            ) : (
                              'Price TBD'
                            )}
                          </div>
                          {project.equipment_provided && (
                            <div className="text-xs text-green-600">
                              Equipment provided
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions Row - Like resource cards */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        {project.attachments && project.attachments.length > 0 ? (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProjectForAttachments(project);
                              setShowAttachmentsModal(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`view-attachments-${project.id}-btn`}
                          >
                            <Paperclip className="w-4 h-4 mr-2" />
                            Documents ({project.attachments.length})
                          </Button>
                        ) : (
                          <span 
                            className="text-xs text-gray-400 flex items-center cursor-pointer hover:text-blue-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProject(project);
                            }}
                            title="Click Edit to add documents"
                          >
                            <Paperclip className="w-4 h-4 mr-1" />
                            No documents - Edit to add
                          </span>
                        )}
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProject(project);
                            }}
                            className="h-8 px-3"
                            data-testid={`edit-project-${project.id}-btn`}
                          >
                            <Edit className="w-4 h-4 text-gray-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateProject(project);
                            }}
                            className="h-8 px-3"
                            title="Duplicate project"
                            data-testid={`duplicate-project-${project.id}-btn`}
                          >
                            <Copy className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id);
                            }}
                            className="h-8 px-3"
                            data-testid={`delete-project-${project.id}-btn`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                          {/* Matching button */}
                          <Button
                            size="sm"
                            variant="default"
                            onClick={(e) => {
                              e.stopPropagation();
                              viewMatches(project.id);
                            }}
                            className="h-8 px-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                            title="Find matching consultants for this project"
                            data-testid={`match-project-${project.id}-btn`}
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
            )}
          </div>
        )}

        {/* Create/Edit Project Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => {
          if (!open) {
            if (editMode) {
              handleEditModalClose();
            } else {
              setShowDialog(false);
              resetForm();
            }
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: '#1B3240' }}>
                {editMode ? 'Edit Project' : 'Create New Project'}
              </DialogTitle>
            </DialogHeader>
            <ProjectForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={editMode ? handleSaveEditedProject : handleSubmit}
              onCancel={() => {
                if (editMode) {
                  handleEditModalClose();
                } else {
                  setShowDialog(false);
                  resetForm();
                }
              }}
              pendingAttachments={pendingAttachments}
              setPendingAttachments={setPendingAttachments}
              editMode={editMode}
            />
          </DialogContent>
        </Dialog>

        {/* Project Attachments Modal */}
        {selectedProjectForAttachments && (
          <ProjectAttachmentsModal
            isOpen={showAttachmentsModal}
            onClose={() => {
              setShowAttachmentsModal(false);
              setSelectedProjectForAttachments(null);
            }}
            projectId={selectedProjectForAttachments.id}
            projectTitle={selectedProjectForAttachments.title}
          />
        )}
        
        {/* Favorite Qualification Modal */}
        <FavoriteQualificationModal
          isOpen={!!showFavoriteModal}
          onClose={() => setShowFavoriteModal(null)}
          onSave={handleSaveFavoriteQualification}
          resourceName={showFavoriteModal?.resourceName}
        />

        {/* Pre-Reservation Modal */}
        <PreReservationModal
          isOpen={!!showPreReservationModal}
          onClose={() => setShowPreReservationModal(null)}
          onConfirm={handleConfirmPreReservation}
          resourceName={showPreReservationModal?.resourceName}
          projectTitle={showPreReservationModal?.projectTitle}
        />
        
        {/* Bonus Trial Modal */}
        <BonusTrialModal
          isOpen={showBonusTrialModal}
          onClose={() => {
            setShowBonusTrialModal(false);
            setBonusTrialOffer(null);
            setPendingActivationProjectId(null);
          }}
          onAccept={handleAcceptBonusTrial}
          offer={bonusTrialOffer}
          loading={bonusTrialLoading}
        />
      </main>
    </div>
  );
};

// Project Form Component (simplified - full version would be larger)
const ProjectForm = ({ formData, setFormData, onSubmit, onCancel, pendingAttachments, setPendingAttachments, editMode = false }) => {
  // Local state for checkboxes
  const [selectedRoles, setSelectedRoles] = React.useState(formData.servicenow_roles || []);
  const [selectedModules, setSelectedModules] = React.useState(formData.servicenow_modules || []);
  const [selectedProducts, setSelectedProducts] = React.useState(formData.servicenow_products || []);
  const [selectedCertifications, setSelectedCertifications] = React.useState(formData.required_certifications || []);
  const [selectedOtherCerts, setSelectedOtherCerts] = React.useState(
    Array.isArray(formData.other_certifications) ? formData.other_certifications : 
    formData.other_certifications ? formData.other_certifications.split(',').map(s => s.trim()).filter(Boolean) : []
  );
  const [moduleSearch, setModuleSearch] = React.useState('');
  const [productSearch, setProductSearch] = React.useState('');
  const [certSearch, setCertSearch] = React.useState('');
  const [roleSearch, setRoleSearch] = React.useState('');
  const [otherCertSearch, setOtherCertSearch] = React.useState('');
  const [showModuleDropdown, setShowModuleDropdown] = React.useState(false);
  const [showCertDropdown, setShowCertDropdown] = React.useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = React.useState(false);
  const [showOtherCertDropdown, setShowOtherCertDropdown] = React.useState(false);

  // Other certifications from servicenowData
  const OTHER_CERTIFICATIONS_LIST = [
    'ITIL 4 Foundation', 'ITIL 4 Managing Professional', 'ITIL 4 Strategic Leader',
    'ITIL v3 Foundation', 'ITIL v3 Intermediate', 'ITIL v3 Expert',
    'PMP (Project Management Professional)', 'PRINCE2 Foundation', 'PRINCE2 Practitioner',
    'Scrum Master (CSM)', 'Product Owner (CSPO)', 'SAFe Agilist', 'SAFe Practitioner', 'PMI-ACP',
    'AWS Solutions Architect', 'AWS Developer', 'AWS SysOps Administrator',
    'Azure Administrator', 'Azure Developer', 'Azure Solutions Architect',
    'Google Cloud Professional', 'Kubernetes Administrator (CKA)', 'Docker Certified Associate', 'Terraform Associate',
    'Microsoft 365 Certified', 'Microsoft Power Platform', 'Microsoft Dynamics 365', 'Microsoft SharePoint',
    'JavaScript Developer', 'Python Developer', 'Java Developer', 'SQL Certified', 'MongoDB Certified',
    'CISSP', 'CISM', 'CompTIA Security+', 'ISO 27001 Lead Auditor', 'ISO 27001 Lead Implementer',
    'Jira Administrator', 'Confluence Administrator', 'Salesforce Administrator', 'SAP Certified',
    'TOGAF Certified', 'Six Sigma Green Belt', 'Six Sigma Black Belt', 'COBIT Foundation'
  ];

  const handleRoleToggle = (roleName) => {
    const newRoles = selectedRoles.includes(roleName)
      ? selectedRoles.filter(r => r !== roleName)
      : [...selectedRoles, roleName];
    setSelectedRoles(newRoles);
    setFormData({ ...formData, servicenow_roles: newRoles });
  };

  const handleModuleToggle = (module) => {
    const newModules = selectedModules.includes(module)
      ? selectedModules.filter(m => m !== module)
      : [...selectedModules, module];
    setSelectedModules(newModules);
    setFormData({ ...formData, servicenow_modules: newModules });
  };

  const handleProductToggle = (productId) => {
    const newProducts = selectedProducts.includes(productId)
      ? selectedProducts.filter(p => p !== productId)
      : [...selectedProducts, productId];
    setSelectedProducts(newProducts);
    setFormData({ ...formData, servicenow_products: newProducts });
  };

  const handleCertToggle = (cert) => {
    const newCerts = selectedCertifications.includes(cert)
      ? selectedCertifications.filter(c => c !== cert)
      : [...selectedCertifications, cert];
    setSelectedCertifications(newCerts);
    setFormData({ ...formData, required_certifications: newCerts });
  };

  const handleOtherCertToggle = (cert) => {
    const newCerts = selectedOtherCerts.includes(cert)
      ? selectedOtherCerts.filter(c => c !== cert)
      : [...selectedOtherCerts, cert];
    setSelectedOtherCerts(newCerts);
    setFormData({ ...formData, other_certifications: newCerts });
  };

  const addCustomOtherCert = (cert) => {
    if (cert && !selectedOtherCerts.includes(cert)) {
      const newCerts = [...selectedOtherCerts, cert];
      setSelectedOtherCerts(newCerts);
      setFormData({ ...formData, other_certifications: newCerts });
      setOtherCertSearch('');
    }
  };

  const filteredOtherCerts = OTHER_CERTIFICATIONS_LIST.filter(c => 
    c.toLowerCase().includes(otherCertSearch.toLowerCase()) && !selectedOtherCerts.includes(c)
  );

  const filteredRoles = SERVICENOW_ROLES.filter(r => 
    r.name.toLowerCase().includes(roleSearch.toLowerCase()) ||
    r.acronym.toLowerCase().includes(roleSearch.toLowerCase())
  );
  
  // Enhanced module search - supports acronym search (e.g., "ITSM", "CSM") and product names
  const filteredModules = SERVICENOW_MODULES.filter(m => {
    const searchLower = moduleSearch.toLowerCase();
    const moduleLower = m.toLowerCase();
    
    // Direct text match
    if (moduleLower.includes(searchLower)) return true;
    
    // Extract acronym from parentheses (e.g., "IT Service Management (ITSM)" -> "ITSM")
    const acronymMatch = m.match(/\(([A-Z]+)\)/);
    if (acronymMatch && acronymMatch[1].toLowerCase().includes(searchLower)) {
      return true;
    }
    
    return false;
  });
  
  const filteredProducts = SERVICENOW_PRODUCTS.filter(p => 
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.fullName.toLowerCase().includes(productSearch.toLowerCase())
  );
  
  // Enhanced certification search - supports acronym search
  const filteredCerts = SERVICENOW_CERTIFICATIONS.filter(c => {
    const searchLower = certSearch.toLowerCase();
    const certLower = c.toLowerCase();
    
    // Direct text match
    if (certLower.includes(searchLower)) return true;
    
    // Extract acronym from certification name
    const acronymMatch = c.match(/\(([A-Z-]+)\)|\b([A-Z]{2,})\b/);
    if (acronymMatch) {
      const acronym = acronymMatch[1] || acronymMatch[2];
      if (acronym && acronym.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    // Check common acronyms
    const certAcronyms = {
      'CIS': 'Certified Implementation Specialist',
      'CAS': 'Certified Application Specialist',
      'CAD': 'Certified Application Developer',
      'CSA': 'Certified System Administrator',
      'ITSM': 'IT Service Management',
      'CSM': 'Customer Service Management',
      'HRSD': 'HR Service Delivery',
      'ITOM': 'IT Operations Management',
      'ITAM': 'IT Asset Management',
      'SPM': 'Strategic Portfolio Management',
      'SecOps': 'Security Operations',
      'GRC': 'Governance Risk and Compliance',
      'FSM': 'Field Service Management'
    };
    
    for (const [acronym, full] of Object.entries(certAcronyms)) {
      if (acronym.toLowerCase().includes(searchLower) && certLower.includes(full.toLowerCase())) {
        return true;
      }
    }
    
    return false;
  });

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">📋 Basic Information</h3>
        <div>
          <Label htmlFor="title">Project Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            required
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="project_type">Project Type *</Label>
            <select
              id="project_type"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={formData.project_type}
              onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
            >
              {PROJECT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="commercial_model">Commercial Model *</Label>
            <select
              id="commercial_model"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={formData.commercial_model}
              onChange={(e) => setFormData({ ...formData, commercial_model: e.target.value })}
            >
              {COMMERCIAL_MODELS.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ServiceNow Products - Primary Matching */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">
          🎯 ServiceNow Products Required
          <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Primary Matching</span>
        </h3>
        <p className="text-xs text-gray-500">
          Select the high-level ServiceNow product areas required for this project. This is the primary signal for consultant matching.
        </p>
        
        <Input
          placeholder="Search products (e.g., ITSM, CSM, HRSD)..."
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          className="mb-3"
        />
        
        {selectedProducts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {selectedProducts.map(productId => {
              const product = SERVICENOW_PRODUCTS.find(p => p.id === productId);
              return product && (
                <span 
                  key={productId} 
                  className="bg-purple-100 text-purple-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium"
                  title={product.fullName}
                >
                  {product.name}
                  <X className="h-3 w-3 cursor-pointer hover:text-purple-600" onClick={() => handleProductToggle(productId)} />
                </span>
              );
            })}
          </div>
        )}
        
        <div className="border rounded-md p-3 bg-gray-50 max-h-48 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {filteredProducts.map((product) => (
              <label
                key={product.id}
                className={`p-2 rounded cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                  selectedProducts.includes(product.id) 
                    ? 'bg-purple-100 border-purple-300 border' 
                    : 'hover:bg-white border border-transparent'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedProducts.includes(product.id)}
                  onChange={() => handleProductToggle(product.id)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span>
                  <strong>{product.name}</strong>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Roles & Skills */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">🎯 Roles & Skills</h3>
        
        {/* ServiceNow Roles */}
        <div>
          <Label>ServiceNow Roles (Required for this project)</Label>
          <Input
            placeholder="Search for a role..."
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            className="mb-2"
          />
          
          {selectedRoles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedRoles.map(roleName => {
                const roleObj = SERVICENOW_ROLES.find(r => r.name === roleName);
                return (
                  <span key={roleName} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    {roleObj ? `${roleObj.acronym} - ${roleObj.name}` : roleName}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleRoleToggle(roleName)} />
                  </span>
                );
              })}
            </div>
          )}
          
          {showRoleDropdown && (
            <div className="border rounded-md p-3 max-h-60 overflow-y-auto bg-gray-50 mb-2">
              {filteredRoles.length === 0 ? (
                <div className="p-2 text-sm text-gray-500">No role found</div>
              ) : (
                <div className="space-y-1">
                  {filteredRoles.map((role) => (
                    <label
                      key={role.id}
                      className="p-2 hover:bg-white cursor-pointer flex items-center space-x-2 rounded block"
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.name)}
                        onChange={() => handleRoleToggle(role.name)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm"><strong className="text-blue-700">{role.acronym}</strong> - {role.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowRoleDropdown(!showRoleDropdown)}
          >
            {showRoleDropdown ? 'Hide roles list' : 'Show roles list'}
          </Button>
        </div>

        {/* ServiceNow Modules */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <Label className="flex items-center gap-2 mb-3">
            ServiceNow Modules to be Mastered
            <span className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded-full">Required</span>
          </Label>
          
          <Input
            placeholder="Search modules by name or product (ITSM, CSM, ITOM...)"
            value={moduleSearch}
            onChange={(e) => {
              setModuleSearch(e.target.value);
              setShowModuleDropdown(true);
            }}
            onFocus={() => setShowModuleDropdown(true)}
            className="mb-3"
          />
          
          {/* Selected modules */}
          {selectedModules.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3 p-2 bg-white rounded-lg border border-blue-100">
              {selectedModules.map(module => (
                <span key={module} className="bg-blue-100 text-blue-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium">
                  {module}
                  <X className="h-3 w-3 cursor-pointer hover:text-blue-600" onClick={() => handleModuleToggle(module)} />
                </span>
              ))}
            </div>
          )}
          
          {/* Module selection dropdown */}
          {showModuleDropdown && (
            <div className="border rounded-lg p-3 bg-white max-h-64 overflow-y-auto mb-2 shadow-sm">
              {filteredModules.length === 0 ? (
                <div className="p-2 text-sm text-gray-500 text-center">No module found for "{moduleSearch}"</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {filteredModules.slice(0, 30).map((module) => (
                    <label
                      key={module}
                      className={`p-2 rounded cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                        selectedModules.includes(module)
                          ? 'bg-blue-100 border-blue-300 border'
                          : 'hover:bg-blue-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModules.includes(module)}
                        onChange={() => handleModuleToggle(module)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-800">{module}</span>
                    </label>
                  ))}
                  {filteredModules.length > 30 && (
                    <div className="col-span-2 text-center text-xs text-gray-500 py-2">
                      +{filteredModules.length - 30} more modules - refine your search
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowModuleDropdown(!showModuleDropdown)}
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            {showModuleDropdown ? 'Hide modules list' : 'Browse all modules'}
          </Button>
        </div>

        {/* ServiceNow Certifications */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <Label className="flex items-center gap-2 mb-3">
            ServiceNow Certifications
            <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded-full">Recommended</span>
          </Label>
          
          <Input
            placeholder="Search by name or acronym (CIS, CSA, CAD, ITSM...)"
            value={certSearch}
            onChange={(e) => {
              setCertSearch(e.target.value);
              setShowCertDropdown(true);
            }}
            onFocus={() => setShowCertDropdown(true)}
            className="mb-3"
          />
          
          {selectedCertifications.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3 p-2 bg-white rounded-lg border border-green-100">
              {selectedCertifications.map(cert => (
                <span key={cert} className="bg-green-100 text-green-800 text-xs px-3 py-1.5 rounded-full flex items-center gap-1 font-medium">
                  {cert.length > 50 ? cert.substring(0, 50) + '...' : cert}
                  <X className="h-3 w-3 cursor-pointer hover:text-green-600" onClick={() => handleCertToggle(cert)} />
                </span>
              ))}
            </div>
          )}
          
          {showCertDropdown && (
            <div className="border rounded-lg p-3 bg-white max-h-64 overflow-y-auto mb-2 shadow-sm">
              {filteredCerts.length === 0 ? (
                <div className="p-2 text-sm text-gray-500 text-center">No certification found for "{certSearch}"</div>
              ) : (
                <div className="grid grid-cols-1 gap-1">
                  {filteredCerts.slice(0, 20).map((cert) => (
                    <label
                      key={cert}
                      className={`p-2 rounded cursor-pointer flex items-center gap-2 text-sm transition-colors ${
                        selectedCertifications.includes(cert)
                          ? 'bg-green-100 border-green-300 border'
                          : 'hover:bg-green-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCertifications.includes(cert)}
                        onChange={() => handleCertToggle(cert)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-gray-800">{cert}</span>
                    </label>
                  ))}
                  {filteredCerts.length > 20 && (
                    <div className="text-center text-xs text-gray-500 py-2">
                      +{filteredCerts.length - 20} more certifications - refine your search
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => setShowCertDropdown(!showCertDropdown)}
            className="border-green-300 text-green-700 hover:bg-green-100"
          >
            {showCertDropdown ? 'Hide certifications list' : 'Browse all certifications'}
          </Button>
        </div>

        <div className="relative bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
          <Label htmlFor="other_certifications" className="flex items-center gap-2 mb-3">
            Other Certifications & Tools (ITIL, Azure, AWS, etc.)
            <span className="text-xs px-2 py-0.5 bg-orange-500 text-white rounded-full">Optional</span>
          </Label>
          
          {/* Selected other certifications */}
          {selectedOtherCerts.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
              {selectedOtherCerts.map(cert => (
                <span key={cert} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                  {cert}
                  <button
                    type="button"
                    onClick={() => handleOtherCertToggle(cert)}
                    className="ml-1 text-orange-600 hover:text-orange-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Search input */}
          <div className="relative">
            <Input
              id="other_certifications"
              value={otherCertSearch}
              onChange={(e) => {
                setOtherCertSearch(e.target.value);
                setShowOtherCertDropdown(true);
              }}
              onFocus={() => setShowOtherCertDropdown(true)}
              placeholder="Search or type custom certification..."
              className="pr-20"
            />
            {otherCertSearch && (
              <button
                type="button"
                onClick={() => addCustomOtherCert(otherCertSearch)}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
              >
                + Add
              </button>
            )}
          </div>
          
          {/* Dropdown */}
          {showOtherCertDropdown && otherCertSearch && filteredOtherCerts.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredOtherCerts.slice(0, 10).map(cert => (
                <button
                  key={cert}
                  type="button"
                  onClick={() => {
                    handleOtherCertToggle(cert);
                    setOtherCertSearch('');
                    setShowOtherCertDropdown(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-orange-50 border-b border-gray-100 last:border-0"
                >
                  {cert}
                </button>
              ))}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-1">Type to search suggestions or add custom certifications</p>
        </div>
      </div>

      {/* Consultant Preferences - AI-Augmented */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">🤖 Consultant Preferences</h3>
        
        <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg border border-purple-100">
          <input
            type="checkbox"
            id="prefer_ai_augmented"
            checked={formData.prefer_ai_augmented || false}
            onChange={(e) => setFormData({ ...formData, prefer_ai_augmented: e.target.checked })}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-5 w-5"
          />
          <label htmlFor="prefer_ai_augmented" className="cursor-pointer">
            <span className="font-medium text-gray-800">Prefer AI-Augmented Consultants</span>
            <p className="text-xs text-gray-600 mt-0.5">
              AI-Augmented consultants use AI tools to boost productivity and deliver more value in less time.
              This is a preference, not a requirement.
            </p>
          </label>
        </div>
        
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>💡 Multiple consultants needed?</strong><br />
            If you need multiple consultants with the same profile, one project is enough.
            If you need different profiles, please create multiple projects for better matching.
          </p>
        </div>
      </div>

      {/* Security Requirements */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-[#1B3240] border-b pb-2">🔐 Security Requirements</h3>
        
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="has_security_requirement"
            checked={formData.has_security_requirement || false}
            onChange={(e) => setFormData({ 
              ...formData, 
              has_security_requirement: e.target.checked,
              required_clearance_level: e.target.checked ? formData.required_clearance_level : null,
              requires_local_presence: e.target.checked ? formData.requires_local_presence : false,
              nationality_restriction: e.target.checked ? formData.nationality_restriction : null
            })}
            className="rounded border-gray-300 h-5 w-5"
          />
          <label htmlFor="has_security_requirement" className="cursor-pointer">
            <span className="font-medium">This project has security requirements</span>
          </label>
        </div>
        
        {formData.has_security_requirement && (
          <div className="ml-8 space-y-4 border-l-2 border-amber-200 pl-4 bg-amber-50/50 py-3 rounded-r-md">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="required_clearance_level">Required Security Clearance</Label>
                <select
                  id="required_clearance_level"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.required_clearance_level || ''}
                  onChange={(e) => setFormData({ ...formData, required_clearance_level: e.target.value })}
                >
                  <option value="">Select clearance level...</option>
                  {SECURITY_CLEARANCE_LEVELS.filter(l => l.id !== 'none').map(level => (
                    <option key={level.id} value={level.id}>{level.name} - {level.description}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="clearance_country">Clearance Must Be Valid In</Label>
                <select
                  id="clearance_country"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.clearance_valid_country || ''}
                  onChange={(e) => setFormData({ ...formData, clearance_valid_country: e.target.value })}
                >
                  <option value="">Select country...</option>
                  {CLEARANCE_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="requires_local_presence"
                checked={formData.requires_local_presence || false}
                onChange={(e) => setFormData({ ...formData, requires_local_presence: e.target.checked })}
                className="rounded border-gray-300 h-5 w-5"
              />
              <label htmlFor="requires_local_presence" className="cursor-pointer text-sm">
                Local physical presence required (cannot work remotely)
              </label>
            </div>
            
            <div>
              <Label htmlFor="nationality_restriction">Nationality Restriction (Optional)</Label>
              <select
                id="nationality_restriction"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.nationality_restriction || ''}
                onChange={(e) => setFormData({ ...formData, nationality_restriction: e.target.value })}
              >
                <option value="">No restriction</option>
                {CLEARANCE_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country} nationals only</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Resource Requirements */}
      <div className="space-y-4">
        <h3 className="font-semibold" style={{ color: '#1B3240' }}>Resource Requirements</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="occupation_rate">Occupation Rate *</Label>
            <select
              id="occupation_rate"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={formData.occupation_rate}
              onChange={(e) => setFormData({ ...formData, occupation_rate: parseInt(e.target.value) })}
            >
              {OCCUPATION_RATES.map(rate => (
                <option key={rate.value} value={rate.value}>{rate.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Enhanced Language Selection with CECRL Levels */}
        <div>
          <Label>Languages Required with Proficiency *</Label>
          <LanguageSelector 
            languages={formData.languages_required}
            onChange={(newLanguages) => setFormData({ ...formData, languages_required: newLanguages })}
          />
          <p className="text-xs text-gray-500 mt-1">💡 Add languages with required CECRL proficiency level (A1-C2)</p>
        </div>
      </div>

      {/* Budget & Timeline - ENHANCED */}
      <div className="space-y-4">
        <h3 className="font-semibold" style={{ color: '#1B3240' }}>Budget & Timeline</h3>
        
        {/* Budget fields */}
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="currency">Currency *</Label>
            <select
              id="currency"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              required
            >
              <option value="">Select currency...</option>
              {CURRENCY_OPTIONS.map(curr => (
                <option key={curr.code} value={curr.code}>{curr.symbol} {curr.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="price_amount">Price Amount *</Label>
            <Input
              id="price_amount"
              type="number"
              step="0.01"
              value={formData.price_amount}
              onChange={(e) => setFormData({ ...formData, price_amount: e.target.value })}
              placeholder="750"
              required
            />
          </div>
          <div>
            <Label htmlFor="price_type">Price Type *</Label>
            <select
              id="price_type"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={formData.price_type}
              onChange={(e) => setFormData({ ...formData, price_type: e.target.value })}
              required
            >
              <option value="">Select price type...</option>
              {PRICE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Hourly equivalent preview */}
        {formData.price_amount && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              💡 <strong>Hourly equivalent:</strong> {
                (() => {
                  const amount = parseFloat(formData.price_amount);
                  let hourly = 0;
                  if (formData.price_type === 'hourly') hourly = amount;
                  else if (formData.price_type === 'daily') hourly = amount / 8;
                  else if (formData.price_type === 'monthly') hourly = amount / 160;
                  else if (formData.price_type === 'fixed' && formData.duration_value) {
                    const durationHours = formData.duration_value * (
                      formData.duration_unit === 'hours' ? 1 :
                      formData.duration_unit === 'days' ? 8 :
                      formData.duration_unit === 'weeks' ? 40 :
                      formData.duration_unit === 'months' ? 160 : 0
                    );
                    hourly = durationHours > 0 ? amount / (durationHours * formData.number_of_consultants) : 0;
                  }
                  const symbol = CURRENCY_OPTIONS.find(c => c.code === formData.currency)?.symbol || formData.currency;
                  return `${symbol}${hourly.toFixed(2)}/h`;
                })()
              }
            </p>
          </div>
        )}
        
        {/* Hide budget option */}
        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="hide_budget"
            checked={formData.hide_budget || false}
            onChange={(e) => setFormData({ ...formData, hide_budget: e.target.checked })}
            className="rounded border-gray-300 h-4 w-4"
          />
          <label htmlFor="hide_budget" className="cursor-pointer text-sm">
            <span className="font-medium">Hide budget from Resource Managers</span>
            <span className="ml-2 text-xs text-gray-500" title="Hiding the budget may reduce matching quality, but protects your budget information during initial discussions">
              ⓘ Budget still used for internal AI matching
            </span>
          </label>
        </div>
        
        {/* Duration fields */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="duration_value">Project Duration *</Label>
            <Input
              id="duration_value"
              type="number"
              step="0.5"
              value={formData.duration_value}
              onChange={(e) => setFormData({ ...formData, duration_value: e.target.value })}
              placeholder="3"
              required
            />
          </div>
          <div>
            <Label htmlFor="duration_unit">Duration Unit *</Label>
            <select
              id="duration_unit"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={formData.duration_unit}
              onChange={(e) => setFormData({ ...formData, duration_unit: e.target.value })}
              required
            >
              <option value="">Select duration unit...</option>
              {DURATION_UNITS.map(unit => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          <Label htmlFor="start_date">Start Date *</Label>
          <Input
            id="start_date"
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            required
          />
        </div>
      </div>

      {/* Location & Work Arrangement - ENHANCED */}
      <div className="space-y-4">
        <h3 className="font-semibold" style={{ color: '#1B3240' }}>Location & Work Arrangement</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="country">Country *</Label>
            <select
              id="country"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              required
            >
              <option value="">Select country...</option>
              {COUNTRIES.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Equipment */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="equipment_provided"
            checked={formData.equipment_provided}
            onChange={(e) => setFormData({ ...formData, equipment_provided: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300"
            style={{ accentColor: '#62D84E' }}
          />
          <Label htmlFor="equipment_provided" className="cursor-pointer">
            Equipment provided by the client
          </Label>
        </div>
        {!formData.equipment_provided && (
          <p className="text-xs text-gray-600 pl-6">📋 Resource provides own equipment</p>
        )}
        
        {/* Remote work */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="remote_possible"
            checked={formData.remote_possible}
            onChange={(e) => setFormData({ ...formData, remote_possible: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300"
            style={{ accentColor: '#62D84E' }}
          />
          <Label htmlFor="remote_possible" className="cursor-pointer">Remote work possible</Label>
        </div>
        
        {formData.remote_possible && (
          <div className="pl-6 space-y-4 border-l-2 border-green-200">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="remote_percentage">Remote Percentage *</Label>
                <div className="flex items-center space-x-3">
                  <input
                    type="range"
                    id="remote_percentage"
                    min="10"
                    max="100"
                    step="10"
                    value={formData.remote_percentage}
                    onChange={(e) => setFormData({ ...formData, remote_percentage: parseInt(e.target.value) })}
                    className="flex-1"
                    style={{ accentColor: '#62D84E' }}
                  />
                  <span className="font-medium text-lg" style={{ color: '#62D84E' }}>
                    {formData.remote_percentage}%
                  </span>
                </div>
              </div>
              <div>
                <Label htmlFor="remote_zone">Remote Zone *</Label>
                <select
                  id="remote_zone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.remote_zone}
                  onChange={(e) => setFormData({ ...formData, remote_zone: e.target.value, remote_zone_detail: '' })}
                  required
                >
                  <option value="">Select remote zone...</option>
                  {REMOTE_ZONES.map(zone => (
                    <option key={zone.value} value={zone.value}>{zone.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Remote zone detail based on selection */}
            {formData.remote_zone === 'country' && (
              <div>
                <Label htmlFor="remote_zone_detail">Select Country *</Label>
                <select
                  id="remote_zone_detail"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.remote_zone_detail}
                  onChange={(e) => setFormData({ ...formData, remote_zone_detail: e.target.value })}
                  required
                >
                  <option value="">Select country...</option>
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            )}
            
            {formData.remote_zone === 'region' && (
              <div>
                <Label htmlFor="remote_zone_detail">Select Region *</Label>
                <select
                  id="remote_zone_detail"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.remote_zone_detail}
                  onChange={(e) => setFormData({ ...formData, remote_zone_detail: e.target.value })}
                  required
                >
                  <option value="">Select region...</option>
                  {REGIONS.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
            )}
            
            {formData.remote_zone === 'other' && (
              <div>
                <Label htmlFor="remote_zone_detail">Specify Zone *</Label>
                <Input
                  id="remote_zone_detail"
                  value={formData.remote_zone_detail}
                  onChange={(e) => setFormData({ ...formData, remote_zone_detail: e.target.value })}
                  placeholder="e.g., EMEA, APAC, etc."
                  required
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="timezone_restrictions">Timezone Restrictions (optional)</Label>
              <Input
                id="timezone_restrictions"
                value={formData.timezone_restrictions}
                onChange={(e) => setFormData({ ...formData, timezone_restrictions: e.target.value })}
                placeholder="e.g., ±2h CET, GMT+1 to GMT+3"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Example: "±2h CET" or "Must overlap 4h with EST"</p>
            </div>
          </div>
        )}
      </div>

      {/* Activation Option */}
      <div className="border-t pt-6">
        <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <input
            type="checkbox"
            id="is_activated"
            checked={formData.is_activated}
            onChange={(e) => setFormData({ ...formData, is_activated: e.target.checked })}
            className="mt-1 w-4 h-4 rounded"
          />
          <div className="flex-1">
            <label htmlFor="is_activated" className="font-medium text-blue-900 cursor-pointer">
              Activate project immediately
            </label>
            <p className="text-sm text-blue-700 mt-1">
              ✓ Your project will be visible to Resource Managers immediately<br/>
              ✗ If unchecked, your project will be saved as a draft and you can activate it later
            </p>
          </div>
        </div>
      </div>

      {/* Attachments Section */}
      <div className="border-t pt-6">
        <Label className="text-base font-semibold mb-3 block">📎 Project Attachments (Optional)</Label>
        <p className="text-sm text-gray-600 mb-4">
          Upload documents related to your project (specifications, requirements, etc.)
        </p>
        
        <div className="space-y-3">
          <input
            type="file"
            id="project-attachments"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
            onChange={(e) => {
              const files = Array.from(e.target.files);
              setPendingAttachments(prev => [...prev, ...files]);
              e.target.value = null; // Reset input
            }}
            className="hidden"
          />
          
          <Button
            type="button"
            variant="outline"
            onClick={() => document.getElementById('project-attachments').click()}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            Select Files
          </Button>

          {/* Display selected files */}
          {pendingAttachments.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Selected files ({pendingAttachments.length}):
              </p>
              {pendingAttachments.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-500">
                      ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPendingAttachments(prev => prev.filter((_, i) => i !== index));
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-500">
            💡 Accepted formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, ZIP (max 10MB per file)
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="btn-snow">
          {editMode ? 'Save Changes' : 'Create Project'}
        </Button>
      </div>
    </form>
  );
};

// Match Card Component
const MatchCard = ({ match, projectId, isFavorite, onToggleFavorite, onPreReserve }) => {
  console.log('🎴 MatchCard rendered:', {
    resourceId: match.resource.id,
    resourceName: match.resource.name,
    isFavorite,
    hasToggleFavorite: !!onToggleFavorite,
    hasPreReserve: !!onPreReserve
  });
  
  return (
    <Card className="bg-white">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="font-semibold" style={{ color: '#1B3240' }}>{match.resource.name}</h4>
            <p className="text-sm text-gray-600">{match.resource.role}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center px-3 py-1 rounded-full" style={{ background: 'rgba(98, 216, 78, 0.1)' }}>
              <Star className="w-4 h-4 mr-1" style={{ color: '#62D84E' }} />
              <span className="text-sm font-semibold" style={{ color: '#62D84E' }}>
                {match.compatibility_score}
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                console.log('💗 Favorite button clicked for resource:', match.resource.id);
                onToggleFavorite();
              }}
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
            </Button>
          </div>
        </div>

        {match.matching_certifications && match.matching_certifications.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">Matching Certifications</p>
            <div className="flex flex-wrap gap-1">
              {match.matching_certifications.map((cert, idx) => (
                <span key={idx} className="px-2 py-1 text-xs rounded" style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}>
                  {cert.split('-')[0].trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {match.matching_skills && match.matching_skills.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">Matching Skills</p>
            <div className="flex flex-wrap gap-1">
              {match.matching_skills.map((skill, idx) => (
                <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-sm mb-3">
          <div className="flex items-center text-gray-600">
            <MapPin className="w-4 h-4 mr-1" />
            {match.resource.location}
          </div>
          <div className="font-semibold" style={{ color: '#1B3240' }}>
            €{match.resource.rate}/day
          </div>
        </div>

        <Button
          className="w-full btn-snow"
          size="sm"
          onClick={() => {
            console.log('📩 Pre-Reserve button clicked for resource:', match.resource.id);
            onPreReserve();
          }}
        >
          <Send className="w-4 h-4 mr-2" />
          Pre-Reserve
        </Button>
      </CardContent>
    </Card>
  );
};

// Language Selector Component with CECRL Levels
const LanguageSelector = ({ languages, onChange }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('B2');

  const handleAdd = () => {
    if (!selectedLanguage) {
      toast.error('Please select a language');
      return;
    }
    
    // Check if language already exists
    if (languages.some(l => l.language === selectedLanguage)) {
      toast.error('Language already added');
      return;
    }

    onChange([...languages, { language: selectedLanguage, level: selectedLevel }]);
    setSelectedLanguage('');
    setSelectedLevel('B2');
  };

  const handleRemove = (index) => {
    onChange(languages.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {/* Add language form */}
      <div className="flex gap-2">
        <div className="flex-1">
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            <option value="">Select language...</option>
            {LANGUAGES.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>
        <div className="w-48">
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
          >
            {CECRL_LEVELS.map(level => (
              <option key={level.value} value={level.value}>{level.label}</option>
            ))}
          </select>
        </div>
        <Button
          type="button"
          onClick={handleAdd}
          size="sm"
          className="btn-snow"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Selected languages list */}
      {languages.length > 0 && (
        <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2">
          {languages.map((lang, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-white px-3 py-2 rounded border border-gray-200"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium" style={{ color: '#1B3240' }}>{lang.language}</span>
                <span className="px-2 py-0.5 text-xs rounded" style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}>
                  {lang.level}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(index)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectsPageNew;
